import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Stack
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from "../contexts/AuthContext";
import { ChangePasswordDialog } from './common/ChangePasswordDialog';

export function UserProfile() {
  const { user, changePassword, isLoading, error, clearError } = useAuth();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const handleOpenChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  const handleCloseChangePassword = () => {
    setIsChangePasswordOpen(false);
  };

  // Handle case where user is not available
  if (!user) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = () => {
    const first = user.given_name?.charAt(0) || '';
    const last = user.family_name?.charAt(0) || '';
    return first + last || user.email.charAt(0).toUpperCase();
  };

  // Format display name
  const getDisplayName = () => {
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    if (user.given_name) {
      return user.given_name;
    }
    return user.email;
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3, bgcolor: 'background.default' }}>
      {/* User Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'primary.main',
            fontSize: '1.25rem',
            fontWeight: 600,
            mr: 2
          }}
        >
          {getInitials()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {getDisplayName()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {user.role && (
              <>
                <BadgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {user.role}
                </Typography>
              </>
            )}
            {user.firmIds && user.firmIds.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">•</Typography>
                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {user.firmIds.length === 1 
                    ? user.firmIds[0] 
                    : `${user.firmIds[0]} (+${user.firmIds.length - 1} more)`
                  }
                </Typography>
                {user.firmIds.length > 1 && (
                  <Typography variant="body2" color="primary.main" sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                    Switch Firm
                  </Typography>
                )}
              </>
            )}
            {user.invitedBy && (
              <>
                <Typography variant="body2" color="text.secondary">•</Typography>
                <Typography variant="body2" color="text.secondary">
                  Created {user.invitedBy}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Contact Details Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
          Contact Details
        </Typography>
        
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  First name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.given_name || 'Not provided'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Last name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.family_name || 'Not provided'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <EmailIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Email
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.email}
              </Typography>
            </Box>
          </Box>

        </Stack>
      </Box>

      {/* Update Password Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Update password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ensure your account is using a long, random password to stay secure.
        </Typography>
        
        <Button
          variant="contained"
          onClick={handleOpenChangePassword}
          sx={{ 
            bgcolor: '#f59e0b', 
            '&:hover': { bgcolor: '#d97706' },
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 600,
            px: 3
          }}
        >
          Update Password
        </Button>
      </Box>

      <ChangePasswordDialog
        open={isChangePasswordOpen}
        onClose={handleCloseChangePassword}
        onChangePassword={changePassword}
        isLoading={isLoading}
        error={error}
        onClearError={clearError}
      />
    </Box>
  );
}